import cloneDeep from 'clone-deep';
import { HomePolicy, HomeProject } from '@models/swagger';
import { VertiIntegrationActionEnum } from '@global/enums';
import { MAX_PREMIUM_THRESHOLD, MIN_PREMIUM_THRESHOLD } from '@global/constants';
import { Store } from '@ngrx/store';
import { EmptyFlowDataGeneral } from '@store/flow-data/general';
import { PolicyClearData } from '@store/flow-data/policy';
import { ProjectClearData } from '@store/flow-data/project';

export const mapProjectToPolicy = (
  project: HomeProject,
  basePolicy: HomePolicy = {},
  overwritePolicy: HomePolicy = {}
): HomePolicy => {
  return {
    ...cloneDeep(basePolicy),
    userId: project.userId ?? basePolicy.userId,
    branchCode: project.branchCode ?? basePolicy.branchCode,
    branchDesc: project.branchDesc ?? basePolicy.branchDesc,
    effectiveDate: project.effectiveDate ?? basePolicy.effectiveDate,
    captureDate: project.captureDate ?? basePolicy.captureDate,
    policyParticipants: project.projectParticipants ?? basePolicy.policyParticipants,
    homeRisks: project.homeRisks ?? basePolicy.homeRisks,
    premiumDetail: project.premiumDetail ?? basePolicy.premiumDetail,
    projectQuoteId: project.projectQuoteId ?? basePolicy.projectQuoteId,
    cancellationDate: project.policyCancellationDate ?? basePolicy.cancellationDate,
    policyId: project.policyId ?? basePolicy.policyId,
    actionId: project.actionId ?? basePolicy.actionId,
    ...cloneDeep(overwritePolicy),
  };
};

export const mapPolicyToProject = (
  policy: HomePolicy,
  baseProject: HomeProject = {},
  overwriteProject: HomeProject = {}
): HomeProject => {
  return {
    ...cloneDeep(baseProject),
    branchCode: policy.branchCode ?? baseProject.branchCode,
    branchDesc: policy.branchDesc ?? baseProject.branchDesc,
    captureDate: policy.captureDate ?? baseProject.captureDate,
    effectiveDate: policy.effectiveDate ?? baseProject.effectiveDate,
    homeRisks: policy.homeRisks ?? baseProject.homeRisks,
    premiumDetail: policy.premiumDetail ?? baseProject.premiumDetail,
    projectParticipants: policy.policyParticipants ?? baseProject.projectParticipants,
    projectQuoteId: policy.projectQuoteId ?? baseProject.projectQuoteId,
    userId: policy.userId ?? baseProject.userId,
    policyCancellationDate: policy.cancellationDate ?? baseProject.policyCancellationDate,
    policyId: policy.policyId ?? baseProject.policyId,
    actionId: policy.actionId ?? baseProject.actionId,
    ...cloneDeep(overwriteProject),
  };
};

export function removeEmptyFields<T>(object: T): T {
  if (object) {
    Object.keys(object).forEach(key => {
      if (object[key] && typeof object[key] === 'object') {
        this.removeEmptyFields(object[key]);
      }
      if (
        object[key] === undefined ||
        object[key] === null ||
        (Object.keys(object[key]).length === 0 && object[key].constructor === Object)
      ) {
        delete object[key];
      }
      if (object[key] instanceof Array) {
        object[key] = object[key].filter(isNotNullish);
      }
    });
  }
  return object;
}

export function isNotNullish<T>(value: T): boolean {
  return value !== undefined && value !== null;
}

/**
 * Get action depending on the value of the difference
 * Calculated with **total** `premiumDetail`, not with `premiumDiff`
 */
export function getPremiumAction(premiumAmn: number | undefined): VertiIntegrationActionEnum {
  if (isNaN(premiumAmn)) {
    return VertiIntegrationActionEnum.Nothing;
  }
  if (premiumAmn >= MIN_PREMIUM_THRESHOLD && premiumAmn <= MAX_PREMIUM_THRESHOLD) {
    return VertiIntegrationActionEnum.Confirm;
  }
  if (premiumAmn > MAX_PREMIUM_THRESHOLD) {
    return VertiIntegrationActionEnum.Pay;
  }
  if (premiumAmn < MIN_PREMIUM_THRESHOLD) {
    return VertiIntegrationActionEnum.Refund;
  }
}

export function deleteSessionStorage(store: Store): void {
  sessionStorage.clear();
  store.dispatch(EmptyFlowDataGeneral());
  store.dispatch(PolicyClearData());
  store.dispatch(ProjectClearData());
}
